import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import { makeAxiosProgress, type UploadProgressFn } from "@/lib/upload-progress";
import type { EventRow, EventType } from "@/lib/types";

export interface EventInput {
  title?: string;
  summary?: string;
  description?: string;
  event_type?: EventType;
  location?: string;
  start_at?: string;         // ISO 8601
  end_at?: string | null;
  registration_url?: string;
  is_published?: boolean;
  is_featured?: boolean;
  image?: File | null;
  /** Optional progress reporter; fires while the multipart body uploads. */
  onProgress?: UploadProgressFn;
}

function buildFormData(input: EventInput): FormData {
  const form = new FormData();
  // Multipart booleans: send as "true"/"false" strings — DRF coerces.
  const append = (k: string, v: unknown) => {
    if (v === undefined) return;
    if (v === null)      return;
    form.append(k, typeof v === "boolean" ? String(v) : String(v));
  };
  append("title",            input.title);
  append("summary",          input.summary);
  append("description",      input.description);
  append("event_type",       input.event_type);
  append("location",         input.location);
  append("start_at",         input.start_at);
  append("end_at",           input.end_at);
  append("registration_url", input.registration_url);
  append("is_published",     input.is_published);
  append("is_featured",      input.is_featured);
  if (input.image) form.append("image", input.image);
  return form;
}

/** POST /api/v1/events/. Director-requested publish gets intercepted
 *  to draft + needs_ceo_approval; CEO+ publishes directly. */
export function useCreateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: EventInput) => {
      const { data } = await api.post<EventRow>("/events/", buildFormData(input), {
        onUploadProgress: makeAxiosProgress(input.onProgress),
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

/** PATCH /api/v1/events/<id>/. */
export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: EventInput }) => {
      const { data } = await api.patch<EventRow>(`/events/${id}/`, buildFormData(input), {
        onUploadProgress: makeAxiosProgress(input.onProgress),
      });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

/** DELETE /api/v1/events/<id>/. */
export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await api.delete(`/events/${id}/`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}


// ---------------------------------------------------------------------------
// Event memories — post-event photo gallery
// ---------------------------------------------------------------------------

import { useQuery } from "@tanstack/react-query";
import type { EventMemoryRow } from "@/lib/types";

/** GET /api/v1/events/memories/recent/?limit=12
 *  Flat feed of recent memory photos across every event the actor can see.
 *  Drives the dashboard's EventMemoriesCard. */
export function useRecentEventMemories(limit = 12) {
  return useQuery<EventMemoryRow[]>({
    queryKey: ["event-memories", "recent", limit],
    queryFn: async () => {
      const { data } = await api.get<EventMemoryRow[]>(
        `/events/memories/recent/?limit=${limit}`,
      );
      return data;
    },
  });
}

/** POST /api/v1/events/<id>/memories/ with multipart `images[]` parts +
 *  optional `caption` field. Returns the created EventMemoryRow[]. */
export function useUploadEventMemories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      { eventId, files, caption }: { eventId: string; files: File[]; caption?: string },
    ) => {
      const form = new FormData();
      for (const f of files) form.append("images", f);
      if (caption) form.append("caption", caption);
      const { data } = await api.post<EventMemoryRow[]>(
        `/events/${eventId}/memories/`, form,
      );
      return { eventId, created: data };
    },
    onSuccess: ({ eventId }) => {
      qc.invalidateQueries({ queryKey: ["events", "detail", eventId] });
      qc.invalidateQueries({ queryKey: ["event-memories"] });
    },
  });
}

/** DELETE /api/v1/events/<id>/memories/<memory_id>/ */
export function useDeleteEventMemory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ eventId, memoryId }: { eventId: string; memoryId: string }) => {
      await api.delete(`/events/${eventId}/memories/${memoryId}/`);
      return { eventId, memoryId };
    },
    onSuccess: ({ eventId }) => {
      qc.invalidateQueries({ queryKey: ["events", "detail", eventId] });
      qc.invalidateQueries({ queryKey: ["event-memories"] });
    },
  });
}
